import { useEffect, useState } from "react";
import { usePersistedState, useScrollRestore } from "@/hooks/useScrollRestore";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScooterCard } from "@/components/ScooterCard";
import { scooterApi, getFileUrl } from "@/lib/api";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Filter,
  LayoutGrid,
  List,
  X,
  Tag,
  Percent,
  Image,
  Video
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

export default function StockListPage() {
  const [scooters, setScooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = usePersistedState("stock:search", "");
  const [statusFilter, setStatusFilter] = usePersistedState("stock:status", "all");
  const [viewMode, setViewMode] = useState("grid");
  const [discountModal, setDiscountModal] = useState(null);
  const [discountPercent, setDiscountPercent] = useState("");
  const [uploading, setUploading] = useState(null);
  useScrollRestore("stock", !loading);

  const API = process.env.REACT_APP_BACKEND_URL || '';

  const handleUploadPhoto = async (scooterId, files, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setUploading(scooterId);
    const token = localStorage.getItem("token");
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("photo_type", "general");
        await axios.post(`${API}/api/scooters/${scooterId}/photos`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
      } catch { toast.error(`Eroare foto: ${file.name}`); }
    }
    toast.success("Fotografii adaugate!");
    setUploading(null);
    fetchScooters();
  };

  const handleUploadVideo = async (scooterId, file, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setUploading(scooterId);
    const token = localStorage.getItem("token");
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/api/scooters/${scooterId}/videos`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success("Video adaugat si comprimat!");
    } catch (err) { toast.error("Eroare upload video"); }
    setUploading(null);
    fetchScooters();
  };

  const handleApplyDiscount = async () => {
    if (!discountModal) return;
    const pct = parseInt(discountPercent);
    if (isNaN(pct) || pct < 0 || pct > 99) {
      toast.error("Procent invalid (0-99). 0 = elimina reducerea");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(`${API}/api/scooters/${discountModal.scooter_id}/discount`, 
        { discount_percent: pct },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setDiscountModal(null);
      setDiscountPercent("");
      fetchScooters();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare");
    }
  };

  useEffect(() => {
    fetchScooters();
  }, [statusFilter]);

  const fetchScooters = async () => {
    try {
      const params = {};
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      const data = await scooterApi.getAll(params);
      setScooters(data);
    } catch (error) {
      toast.error("Failed to load scooters");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!search.trim()) {
      fetchScooters();
      return;
    }
    setLoading(true);
    try {
      const data = await scooterApi.getAll({ search: search.trim() });
      setScooters(data);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    fetchScooters();
  };

  const filteredScooters = scooters.filter(s => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      s.brand.toLowerCase().includes(searchLower) ||
      s.model.toLowerCase().includes(searchLower) ||
      s.vin.toLowerCase().includes(searchLower) ||
      s.scooter_id.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="stock-list">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-2xl md:text-3xl">
              Stock / Stoc
            </h1>
            <p className="text-muted-foreground text-sm">
              {filteredScooters.length} scooter{filteredScooters.length !== 1 ? "s" : ""} in inventory
            </p>
          </div>
          <Link to="/add-scooter">
            <Button 
              className="bg-primary text-black font-bold uppercase tracking-wide hover:bg-primary/90"
              data-testid="btn-add-new"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by Brand, Model, VIN, or ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="pl-10 font-mono"
                    data-testid="input-search"
                  />
                </div>
                <Button variant="outline" onClick={handleSearch} data-testid="btn-search">
                  <Search className="w-4 h-4" />
                </Button>
              </div>

              {/* Status Filter */}
              <Select
                value={statusFilter}
                onValueChange={setStatusFilter}
              >
                <SelectTrigger className="w-full md:w-[200px]" data-testid="select-filter-status">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="in_stock">In Stock</SelectItem>
                  <SelectItem value="reserved">Reserved</SelectItem>
                  <SelectItem value="in_service">In Service</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                  <SelectItem value="casse">Casse</SelectItem>
                </SelectContent>
              </Select>

              {/* View Mode Toggle */}
              <div className="flex border border-border rounded-md">
                <Button
                  variant={viewMode === "grid" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className="rounded-r-none"
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="rounded-l-none"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>

              {/* Clear Filters */}
              {(search || statusFilter !== "all") && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse text-primary font-mono">Loading...</div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredScooters.length === 0 && (
          <Card className="border-border border-dashed">
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground mb-4">No scooters found</p>
              <Link to="/add-scooter">
                <Button className="bg-primary text-black font-bold">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Scooter
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Grid View */}
        {!loading && viewMode === "grid" && filteredScooters.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredScooters.map((scooter) => (
              <div key={scooter.id} className="relative group">
                <ScooterCard scooter={scooter} />
                {/* Quick action buttons overlay */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <label className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                    <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => handleUploadPhoto(scooter.scooter_id, Array.from(e.target.files), e)} />
                    <div className="bg-black/70 hover:bg-primary text-white w-8 h-8 rounded-full flex items-center justify-center transition-colors" title="Adauga foto">
                      <Image className="w-4 h-4" />
                    </div>
                  </label>
                  <label className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                    <input type="file" accept="video/*" className="hidden" onChange={(e) => { if (e.target.files[0]) handleUploadVideo(scooter.scooter_id, e.target.files[0], e); }} />
                    <div className="bg-black/70 hover:bg-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center transition-colors" title="Adauga video">
                      <Video className="w-4 h-4" />
                    </div>
                  </label>
                  <div className="bg-black/70 hover:bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-colors" title="Reducere" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDiscountModal(scooter); setDiscountPercent(String(scooter.discount_percent || "")); }}>
                    <Percent className="w-4 h-4" />
                  </div>
                </div>
                {uploading === scooter.scooter_id && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-xl z-20">
                    <span className="text-white text-sm animate-pulse">Se incarca...</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* List View */}
        {!loading && viewMode === "list" && filteredScooters.length > 0 && (
          <div className="space-y-2">
            {filteredScooters.map((scooter) => (
              <Link key={scooter.id} to={`/stock/${scooter.scooter_id}`}>
                <Card className="border-border hover:border-primary/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-md overflow-hidden bg-secondary flex-shrink-0">
                        <img
                          src={scooter.photos?.[0] 
                            ? getFileUrl(scooter.photos[0])
                            : "https://images.unsplash.com/photo-1608551883326-2c9bd5d52618?w=100&h=100&fit=crop"
                          }
                          alt={`${scooter.brand} ${scooter.model}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-primary">{scooter.scooter_id}</span>
                          <span className="w-2 h-2 rounded-full" style={{ 
                            backgroundColor: scooter.status === "in_stock" ? "#BEF264" :
                              scooter.status === "reserved" ? "#FACC15" :
                              scooter.status === "in_service" ? "#38BDF8" :
                              scooter.status === "casse" ? "#F97316" : "#EF4444"
                          }} />
                        </div>
                        <h3 className="font-heading font-bold truncate">
                          {scooter.brand} {scooter.model}
                        </h3>
                        <p className="text-sm text-muted-foreground font-mono">
                          {scooter.year} • {scooter.engine_cc}cc • {scooter.mileage.toLocaleString()} km
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        {scooter.discount_percent > 0 && (
                          <Badge className="bg-red-500/20 text-red-400 text-xs">-{scooter.discount_percent}%</Badge>
                        )}
                        <div>
                          <p className="font-mono font-bold text-primary text-lg">
                            {scooter.selling_price?.toLocaleString()} EUR
                          </p>
                          {scooter.old_price > 0 && (
                            <p className="text-xs text-muted-foreground line-through">{scooter.old_price} EUR</p>
                          )}
                          <p className="text-xs text-muted-foreground uppercase">
                            {scooter.status?.replace("_", " ")}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" className="h-8 px-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDiscountModal(scooter); setDiscountPercent(String(scooter.discount_percent || "")); }}>
                          <Percent className="w-4 h-4" />
                        </Button>
                        <label className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                          <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => { e.stopPropagation(); handleUploadPhoto(scooter.scooter_id, Array.from(e.target.files), e); }} />
                          <Button size="sm" variant="ghost" className="h-8 px-2" asChild disabled={uploading === scooter.scooter_id}>
                            <span><Image className="w-4 h-4" /></span>
                          </Button>
                        </label>
                        <label className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                          <input type="file" accept="video/*" className="hidden" onChange={(e) => { e.stopPropagation(); if (e.target.files[0]) handleUploadVideo(scooter.scooter_id, e.target.files[0], e); }} />
                          <Button size="sm" variant="ghost" className="h-8 px-2" asChild disabled={uploading === scooter.scooter_id}>
                            <span><Video className="w-4 h-4" /></span>
                          </Button>
                        </label>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      {/* Discount Modal */}
      <Dialog open={!!discountModal} onOpenChange={() => setDiscountModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-red-400" />
              Reducere / Promotie
            </DialogTitle>
          </DialogHeader>
          {discountModal && (
            <div className="space-y-4">
              <div className="bg-secondary/30 rounded-lg p-3">
                <p className="font-semibold">{discountModal.brand} {discountModal.model}</p>
                <p className="text-sm text-muted-foreground">Pret actual: {discountModal.selling_price} EUR</p>
                {discountModal.old_price > 0 && (
                  <p className="text-sm text-red-400">Pret original: {discountModal.old_price} EUR (-{discountModal.discount_percent}%)</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Procent reducere (%)</label>
                <Input 
                  type="number" 
                  value={discountPercent} 
                  onChange={(e) => setDiscountPercent(e.target.value)} 
                  placeholder="Ex: 20 (0 = elimina reducerea)"
                  min="0" max="99"
                />
                {discountPercent && parseInt(discountPercent) > 0 && (
                  <p className="text-sm text-emerald-400">
                    Noul pret: {Math.round((discountModal.old_price || discountModal.selling_price) * (1 - parseInt(discountPercent) / 100))} EUR
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleApplyDiscount} className="flex-1 bg-red-600 hover:bg-red-700">
                  <Tag className="w-4 h-4 mr-1" /> Aplica reducere
                </Button>
                {discountModal.discount_percent > 0 && (
                  <Button variant="outline" onClick={() => { setDiscountPercent("0"); handleApplyDiscount(); }}>
                    Elimina
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </Layout>
  );
}
